import { useQuery, useMutation, MutationFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  RolesData,
  RoleData,
  RoleWithPermissionsData,
  RoleWithPermissions,
} from "../utils/app-types";
import { BASE_URL } from "./index";
import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import { RolesModel } from "../models/roles/roles.model";

/**
 * Hook to fetch a paginated list of roles.
 *
 * @param {number} [page=1] - Page number for pagination.
 * @param {number} [itemsPerPage=10] - Number of items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} A React Query object with roles list and a `refetch` function.
 */
export const useFetchRoles = (
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = ["roles", page, itemsPerPage, JSON.stringify(filters), sort];
  const { refetch, ...queryResult } = useQuery<
    IResponse<RolesModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      page: page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const roles = await ApiService.get(`/roles`, { params });
    return roles.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch all roles without pagination.
 *
 * @returns {Object} A React Query object with all roles and a `refetch` function.
 */
export const useFetchRolesWithoutPagination = () => {
  const queryKey = ["roles"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<RolesModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`/roles?pagination=0`));
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of basic (default) roles.
 *
 * @returns {Object} A React Query object with basic roles and a `refetch` function.
 */
export const useFetchBasicRoles = () => {
  const queryKey = ["basic-roles"];
  const { refetch, ...queryResult } = useQuery<RolesData, AxiosError>(
    queryKey,
    async () => {
      const roles = await ApiService.get(`/roles/basic`);
      return roles;
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of extra (custom) roles.
 *
 * @returns {Object} A React Query object with extra roles and a `refetch` function.
 */
export const useFetchExtraRoles = () => {
  const queryKey = ["extra-roles"];
  const { refetch, ...queryResult } = useQuery<RolesData, AxiosError>(
    queryKey,
    async () => {
      const roles = await ApiService.get(`/roles/extra`);
      return roles;
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch the details of a specific role, including its permissions.
 *
 * @param {string} [id] - Role ID.
 * @returns {Object} A React Query object with role details and a `refetch` function.
 */
export const useFetchRole = (id?: string) => {
  const queryKey = ["role", id];
  const { refetch, ...queryResult } = useQuery<
    RoleWithPermissionsData,
    AxiosError
  >(
    queryKey,
    async () => {
      const role = await ApiService.get(`${BASE_URL}/roles/${id}`);
      return role;
    },
    {
      staleTime: 60 * 1000,
      enabled: Boolean(id),
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new role (extra/custom role).
 *
 * @returns {Object} A React Query mutation object to create a new role.
 */
export const useCreateExtraRole = () => {
  const createExtraRole: MutationFunction<
    IResponse<RoleWithPermissions>,
    FormData
  > = async (postData: any) => {
    const response = await ApiService.post(`${BASE_URL}/roles`, postData);
    return response.data;
  };
  return useMutation(createExtraRole);
};

/**
 * Hook to update an existing role's permissions.
 *
 * @param {string} [id] - Role ID.
 * @returns {Object} A React Query mutation object to update role permissions.
 */
export const useUpdateRole = (id?: string) => {
  const updateRole: MutationFunction<IResponse<RoleData>, FormData> = async (
    updatedData: any
  ) => {
    const response = await ApiService.put(
      `${BASE_URL}/roles/${id}/permissions`,
      updatedData
    );
    return response.data;
  };
  return useMutation(updateRole);
};
